import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { EndpointStats } from '../../types';
import {
  CHART_AXIS_PROPS,
  CHART_AXIS_TICK,
  CHART_BAR_PROPS,
  CHART_GRID_PROPS,
  CHART_PROVIDER_COLORS,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from './chartTheme';
import {
  ChartBarGradient,
  ChartEmptyState,
  ChartPanel,
} from './ChartPrimitives';
import { chartGradientFill, useChartGradientId } from './chartGradient';

interface EndpointDurationChartProps {
  stats: EndpointStats[];
  title?: string;
}

function shortenEndpoint(endpoint: string, provider: 'fal' | 'gemini'): string {
  const prefix = provider === 'fal' ? 'fal' : 'gemini';
  const name = endpoint.replace(/^fal-ai\//, '').replace(/-preview$/, '');
  return `${prefix}: ${name}`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

export function EndpointDurationChart({ stats, title = 'Endpoint Avg Duration' }: EndpointDurationChartProps) {
  const falGradientId = useChartGradientId('endpoint-fal-duration');
  const geminiGradientId = useChartGradientId('endpoint-gemini-duration');
  const data = stats
    .filter((s) => s.avgDuration > 0)
    .map((s) => ({
      name: shortenEndpoint(s.endpoint, s.provider),
      avgDuration: s.avgDuration,
      provider: s.provider,
      jobsCompleted: s.jobsCompleted,
    }));

  const hasData = data.length > 0;

  return (
    <ChartPanel
      title={title}
      iconClassName="bg-warning-50 text-warning-700"
      icon={
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      }
      summary={
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: CHART_PROVIDER_COLORS.fal }} />
            Fal
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: CHART_PROVIDER_COLORS.gemini }} />
            Gemini
          </span>
        </div>
      }
    >

      {hasData ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 25 }}>
              <defs>
                <ChartBarGradient id={falGradientId} color={CHART_PROVIDER_COLORS.fal} />
                <ChartBarGradient id={geminiGradientId} color={CHART_PROVIDER_COLORS.gemini} />
              </defs>
              <CartesianGrid {...CHART_GRID_PROPS} />
              <XAxis
                {...CHART_AXIS_PROPS}
                dataKey="name"
                tick={CHART_AXIS_TICK}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
              />
              <YAxis
                {...CHART_AXIS_PROPS}
                tick={CHART_AXIS_TICK}
                width={35}
                tickFormatter={(v: number) => formatDuration(v)}
              />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                formatter={(value: number) => [formatDuration(value), 'Avg Duration']}
                labelFormatter={(label, payload) => {
                  if (payload && payload.length > 0) {
                    const item = payload[0].payload as (typeof data)[number];
                    return `${label} (${item.jobsCompleted} completed jobs)`;
                  }
                  return String(label);
                }}
              />
              <Bar
                {...CHART_BAR_PROPS}
                dataKey="avgDuration"
                radius={[3, 3, 0, 0]}
                isAnimationActive={false}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={chartGradientFill(
                      entry.provider === 'fal' ? falGradientId : geminiGradientId,
                    )}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <ChartEmptyState>No endpoint duration data available</ChartEmptyState>
      )}
    </ChartPanel>
  );
}
